import React, { useState, useEffect } from 'react'
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth'
import { currentSessionDecoded, isAdmin, isCaptain } from 'helpers/auth-helper'

const useAuth = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(false)
    const [user, setUser] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [sessionData, setSessionData] = useState(null) // Add sessionData state
    const [isAdminUser, setIsAdminUser] = useState(false)
    const [isCaptainUser, setIsCaptainUser] = useState(false)

    useEffect(() => {
        const initializeAuth = async () => {
            setIsLoading(true)
            await checkAuthStatus()
            await checkSessionStatus()
            setIsLoading(false)
        }
        initializeAuth()
    }, [])

    const checkAuthStatus = async () => {
        try {
            const currentUser = await getCurrentUser()

            setIsAuthenticated(true)
            setUser(currentUser)
        } catch (error) {
            setIsAuthenticated(false)
            setUser(null)
        }
    }

    const checkSessionStatus = async () => {
        try {
            const session = await currentSessionDecoded()
            if (session) {
                setSessionData(session) // Set session data
                setIsAdminUser(isAdmin(session))
                setIsCaptainUser(isCaptain(session))
            }
        } catch (error) {}
    }

    const checkIsAuthorized = () => {
        if (!sessionData?.['cognito:groups']) return false
        return (
            sessionData['cognito:groups'].includes('admin') ||
            sessionData['cognito:groups'].includes('counter_agent') ||
            sessionData['cognito:groups'].includes('check_in_agent') ||
            sessionData['cognito:groups'].includes('port_captain') ||
            sessionData['cognito:groups'].includes('finance') ||
            sessionData['cognito:groups'].includes('management') ||
            sessionData['cognito:groups'].includes('bh_nsa') ||
            sessionData['cognito:groups'].includes('qa_nsa') ||
            sessionData['cognito:groups'].includes('customer_service_international') ||
            sessionData['cognito:groups'].includes('customer_service_domestic')
        )
    }

    const canAccessRoute = path => {
        if (!sessionData?.['cognito:groups']) return false

        // Admin has access to all routes
        if (sessionData['cognito:groups'].includes('admin')) return true

        const isCounterAgent = sessionData['cognito:groups'].includes('counter_agent')
        const isCheckInAgent = sessionData['cognito:groups'].includes('check_in_agent')
        const isPortCaptain = sessionData['cognito:groups'].includes('port_captain')
        const isFinanceTeam = sessionData['cognito:groups'].includes('finance')
        const isManagement = sessionData['cognito:groups'].includes('management')
        const isNSATeam = sessionData['cognito:groups'].includes('bh_nsa') || sessionData['cognito:groups'].includes('qa_nsa')
        const isCustomerServiceInternational = sessionData['cognito:groups'].includes('customer_service_international')
        const isCustomerServiceDomestic = sessionData['cognito:groups'].includes('customer_service_domestic')

        console.log('User roles:', {
            isCounterAgent,
            isCheckInAgent,
            isPortCaptain,
            isFinanceTeam,
            isManagement,
            isNSATeam,
            isCustomerServiceInternational,
            isCustomerServiceDomestic,
        })

        // Define route access for each role
        const counterAgentRoutes = ['/boat-booking', '/manage-boat-booking', '/check-in', '/']
        const checkInAgentRoutes = ['/check-in', '/manage-boat-booking', '/']
        const customerServiceRoutes = ['/boat-booking', '/manage-boat-booking', '/']
        const portCaptainRoutes = ['/route-operations', '/vessel/checklist', '/vessel/defect-list', '/feedback', '/']
        const financeTeamRoutes = [
            '/reports/day-pass',
            '/reports/bookings',
            '/reports/trips',
            '/reports/route-performance',
            '/reports/station-revenue',
            '/reports/fleet-performance',
            '/reports/hourly-booking-performance',
            '/reports/customer-feedback',
            '/reports/nationality-summary',
            '/wallets/list',
            '/wallets/detail',
            '/',
        ]
        const managementRoutes = [
            '/dashboard',
            '/dashboard/revenue',
            '/reports/bookings',
            '/reports/day-pass',
            '/reports/trips',
            '/reports/route-performance',
            '/reports/station-revenue',
            '/reports/fleet-performance',
            '/reports/hourly-booking-performance',
            '/reports/customer-feedback',
            '/reports/nationality-summary',
            '/explore-items',
            '/',
        ]
        const nsaTeamRoutes = [
            '/passenger-record',
        ]

        // Combine allowed routes based on user's roles
        let allowedRoutes = []

        if (isCounterAgent) {
            allowedRoutes = [...allowedRoutes, ...counterAgentRoutes]
        }

        if (isCheckInAgent) {
            allowedRoutes = [...allowedRoutes, ...checkInAgentRoutes]
        }

        if (isPortCaptain) {
            allowedRoutes = [...allowedRoutes, ...portCaptainRoutes]
        }

        if (isFinanceTeam) {
            allowedRoutes = [...allowedRoutes, ...financeTeamRoutes]
        }

        if (isManagement) {
            allowedRoutes = [...allowedRoutes, ...managementRoutes]
        }

        if (isNSATeam) {
            allowedRoutes = [...allowedRoutes, ...nsaTeamRoutes]
        }

        if (isCustomerServiceInternational || isCustomerServiceDomestic) {
            allowedRoutes = [...allowedRoutes, ...customerServiceRoutes]
        }

        return allowedRoutes.some(route => path.startsWith(route))
    }

    const isNSAUser = () => {
        if (!sessionData?.['cognito:groups']) return false
        return (
            sessionData['cognito:groups'].includes('bh_nsa') ||
            sessionData['cognito:groups'].includes('qa_nsa')
        )
    }

    return {
        isAuthenticated,
        user,
        isLoading,
        isAdminUser,
        isCaptainUser,
        checkIsAuthorized,
        canAccessRoute,
        isNSAUser,
        sessionData,
    }
}

export default useAuth
